import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  createDeliveryProvider,
  deleteDeliveryProvider,
  listDeliveryProviders,
  testDeliveryProviderConnection,
  updateDeliveryProvider,
} from '../../api/deliverySettingsApi';
import {
  settingsKeys,
  useDeliverySettings,
  useSettingsQuery,
  useUpdateDeliverySettings,
} from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { DeliveryModel, ProviderSummary } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  MultiToggleField,
  ProviderManager,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { CURRENCY_OPTIONS, DELIVERY_MODEL_OPTIONS } from '../options';

/** Delivery models, providers, credentials and fallbacks. */
export function DeliverySettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('delivery');
  const query = useDeliverySettings();
  const update = useUpdateDeliverySettings();
  const form = useSettingsForm(query.data);
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  // Shared cache entry with the ProviderManager — used to populate the fallback select.
  const providersQuery = useSettingsQuery<ProviderSummary[]>(
    settingsKeys.deliveryProviders,
    listDeliveryProviders,
  );
  const fallbackOptions = (providersQuery.data ?? []).map((provider) => ({
    value: provider.id,
    label: t('deliverySettings.rulesSection.fallbackProviderOptionLabel', {
      name: provider.name,
      code: provider.code,
    }),
  }));

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('deliverySettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('deliverySettings.title')}
      subtitle={t('deliverySettings.subtitle')}
      icon={<LocalShippingRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('deliverySettings.modelsSection.title')} icon={<LocalShippingRoundedIcon />}>
              <MultiToggleField
                label={t('deliverySettings.modelsSection.enabledModelsLabel')}
                description={t('deliverySettings.modelsSection.enabledModelsDescription')}
                value={draft.enabledModels}
                options={DELIVERY_MODEL_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('enabledModels', value as DeliveryModel[])}
                disabled={disabled}
              />
              <SwitchField
                label={t('deliverySettings.modelsSection.courierLabel')}
                checked={draft.courierEnabled}
                onChange={(value) => form.setField('courierEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('deliverySettings.modelsSection.pickupLabel')}
                checked={draft.pickupEnabled}
                onChange={(value) => form.setField('pickupEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('deliverySettings.modelsSection.sameDayLabel')}
                checked={draft.sameDayEnabled}
                onChange={(value) => form.setField('sameDayEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('deliverySettings.rulesSection.title')} icon={<TuneRoundedIcon />}>
              <SelectField
                label={t('deliverySettings.rulesSection.defaultCurrencyLabel')}
                value={draft.defaultCurrency}
                options={CURRENCY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('defaultCurrency', value)}
                disabled={disabled}
              />
              <SelectField
                label={t('deliverySettings.rulesSection.fallbackProviderLabel')}
                description={t('deliverySettings.rulesSection.fallbackProviderDescription')}
                value={draft.fallbackProviderId ?? ''}
                options={fallbackOptions}
                onChange={(value) => form.setField('fallbackProviderId', value || null)}
                disabled={disabled}
                allowEmpty
                emptyLabel={t('deliverySettings.rulesSection.fallbackProviderEmptyLabel')}
              />
              <ChipListField
                label={t('deliverySettings.rulesSection.restrictionsLabel')}
                description={t('deliverySettings.rulesSection.restrictionsDescription')}
                value={draft.restrictions}
                onChange={(value) => form.setField('restrictions', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <ProviderManager
              title={t('deliverySettings.providersTitle')}
              description={t('deliverySettings.providersDescription')}
              icon={<LocalShippingRoundedIcon />}
              listKey={settingsKeys.deliveryProviders}
              api={{
                list: listDeliveryProviders,
                create: createDeliveryProvider,
                update: updateDeliveryProvider,
                remove: deleteDeliveryProvider,
                test: testDeliveryProviderConnection,
              }}
              canManage={canManage}
              canManageCredentials={canManageCredentials}
              credentialHint={t('deliverySettings.providersCredentialHint')}
              onFeedback={notify}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
